<template>
<div class="level-item">
    <div v-if="!isNetBaseAuthenticated">
        <button type="submit" class="button is-primary is-outlined" @click="showNetbaseAuthInstruction" :disabled="isCheckingToken">
            Create New Project
        </button>
    </div>
    <div v-if="isNetBaseAuthenticated" class="level-item">
        <span class="button is-static level-item">
            <span class="icon is-small">
                <i class="fas fa-check"></i>
            </span>
            <span>NetBase Authenticated</span>
        </span>
        <router-link v-if="$can('create','Project')" class="level-item button is-primary is-outlined" to="/project/create">
            Create New Project
        </router-link>
    </div>
    <div>
        <b-loading :is-full-page="isFullPage" :active.sync="isAuthInProgress">
            <div>
                <span>
                    NetBase Authentication In Progress...
                </span>
                <b-icon
                    pack="fas"
                    icon="sync-alt"
                    size="is-small"
                    custom-class="fa-spin">
                </b-icon>
            </div>
        </b-loading>
    </div>
</div>
</template>

<script>
import axios from '@/services/axios-auth-wrapper';
import uuidv1 from 'uuid/v1';
import ability from '@/ability.js';

const clientId = process.env.VUE_APP_CONVERSABILITY_NETBASE_CLIENT_ID;
const conversabilityNetBaseAuthApiUri = process.env.VUE_APP_CONVERSABILITY_API_URL + '/api/netbase';
const netBaseAuthoriseUri = 'https://api.netbase.com/cb/oauth/authorize';
const isNetBaseAuthenticatedKey = 'isNetBaseAuthenticated';
const netBaseFingerprintKey = 'netBaseFingerprint';
const toastDefaultOptions = { duration: 5000, position: 'is-bottom-right' };

export default {
    name: 'NetBaseAuth',
    data () {
        let extractedPrintAndCode = extractPrintAndCodeFromQuery(this.$route.query);
        this.$router.replace({ query: extractedPrintAndCode.trimmedQuery });
        return {
            fingerprint: extractedPrintAndCode.fingerprint,
            netBaseAuthCode: extractedPrintAndCode.code,
            isCheckingToken: false,
            isNetBaseAuthenticated: JSON.parse(localStorage.getItem(isNetBaseAuthenticatedKey)),
            isFullPage: true,
            isAuthInProgress: false
        };
    },
    created () {
        let vueContext = this;
        let userId = localStorage.getItem('userId');
        if (vueContext.isNetBaseAuthenticated == null) {
            checkIfUserAlreadyHaseNetBaseAccessToken(vueContext, userId);
        } else {
            if (vueContext.fingerprint && vueContext.netBaseAuthCode && !vueContext.isNetBaseAuthenticated) {
                // This has been returned from netbase aunthentication service
                // Validate the fingerprint to check whether the caller and the receiver is the same.
                let savedFingerprint = localStorage.getItem(netBaseFingerprintKey);
                if (savedFingerprint === vueContext.fingerprint) {
                    // Now we need to retrieve the access token and save it in the Database.
                    // This is done in the Conversability Api, so all we need to do is post the temp auth code to the Api along with userId, fingerprint and callbakUri.
                    // The callbackUri must be the same uri that was used in the inital netbase authentication without the fingerprint query string.
                    let netBaseTokenRequest = {
                        userId: userId,
                        fingerprint: vueContext.fingerprint,
                        AuthCode: vueContext.netBaseAuthCode,
                        callbackUri: window.location.href
                    };
                    generateNetBaseAccessToken(vueContext, netBaseTokenRequest);
                } else {
                    // unable to authenticate netbase due to fingerprint mismatch.
                    showUnableToAuthenticateNetBaseToast(vueContext);
                }
            }
        }
    },
    methods: {
        getAuthorisationUri () {
            var fingerprint = generateFingerprint();
            let currentUri = window.location.href;
            let callbackUri = `${currentUri}?fingerprint=${fingerprint}`;
            let queryString = `?response_type=code&client_id=${clientId}&redirect_uri=${callbackUri}`;
            return netBaseAuthoriseUri + queryString;
        },
        showNetbaseAuthInstruction () {
            let title = 'NetBase Authentication Required';
            let message = `Before you can create a project you will need to authenticate a NetBase account.
                The list of offices with existing NetBase licenses can be found in the FAQ
                (see <i class="far fa-question-circle"></i> by your email address in the top right).
                If your office doesn't have a NetBase license you'll need to contact the social intelligance
                hub in Sofia (<a href="mailto:conversability@publicis.bg">conversability@publicis.bg</a>) to discuss your options.
                <br /> <br />
                Assuming that you have access to a NetBase account, in order for it to be used you will need to
                contact your NetBase account manager and ask them to enable API access. This must be done on the
                NetBase side before continuing in order for authentication to be successful.`;
            this.$buefy.dialog.confirm({
                title: title,
                message: message,
                confirmText: 'Authenticate NetBase',
                cancelText: 'Cancel',
                type: 'is-info',
                hasIcon: true,
                onConfirm: () => {
                    window.location = this.getAuthorisationUri();
                }
            });
        }
    }
};

// Private functions
function checkIfUserAlreadyHaseNetBaseAccessToken (vueContext, userId) {
    vueContext.isCheckingToken = true;
    let uri = `${conversabilityNetBaseAuthApiUri}/isauthorised/${userId}`;
    axios.get(uri)
        .then(response => {
            // The user already has a netbase access token.
            // We can set a flag in the local storage to specify that the user is NetBase authenticated so that we don't need to re-authenticate
            if (response.status === 200) {
                setNetBaseAuthenticated(vueContext, true);
            }
            vueContext.isCheckingToken = false;
        },
        error => {
            if (error) {
                setNetBaseAuthenticated(vueContext, false);
            }
            vueContext.isCheckingToken = false;
        });
}

function generateNetBaseAccessToken (vueContext, netBaseTokenRequest) {
    vueContext.isAuthInProgress = true;
    let uri = `${conversabilityNetBaseAuthApiUri}/generatetoken`;
    axios.post(uri, netBaseTokenRequest)
        .then(response => {
            // The user should now have a netbase access token.
            // We can set a flag in the local storage to specify that the user is NetBase authenticated so that we don't need to re-authenticate
            if (response.status === 200) {
                setNetBaseAuthenticated(vueContext, true);
            }
            // If the user role is changed then we need to update the rules in the ability.
            if (response.data && response.data.userRole && localStorage.getItem('role') !== response.data.userRole) {
                localStorage.setItem('role', response.data.userRole);
                vueContext.$ability.update(ability[response.data.userRole].rules);
            }
            // When this function is called we are certain that the user has clicked on Create Project button,
            // therefore once the NetBase token is generated successfully we need navigate the user to the Project Create screen.
            vueContext.$router.push('project/create');
            vueContext.isAuthInProgress = false;
        },
        error => {
            if (error) {
                setNetBaseAuthenticated(vueContext, false);
                showUnableToAuthenticateNetBaseToast(vueContext);
            }
            vueContext.isAuthInProgress = false;
        });
}

function setNetBaseAuthenticated (vueContext, val) {
    localStorage.setItem(isNetBaseAuthenticatedKey, val);
    vueContext.isNetBaseAuthenticated = val;
}

function showUnableToAuthenticateNetBaseToast (vueContext) {
    vueContext.$buefy.toast.open({ ...toastDefaultOptions, message: 'Unable to authenticate NetBase', type: 'is-danger' });
}

function generateFingerprint () {
    var fingerprint = uuidv1();
    localStorage.setItem(netBaseFingerprintKey, fingerprint);
    return fingerprint;
}

function extractPrintAndCodeFromQuery (query) {
    let fingerprint = query.fingerprint;
    let code = query.code;
    let modifiedQuery = Object.assign({}, query);
    delete modifiedQuery.fingerprint;
    delete modifiedQuery.code;
    return {
        fingerprint: fingerprint,
        code: code,
        trimmedQuery: modifiedQuery
    };
}

</script>
<!--Non-Scoped style-->
<style lang="scss">
.dialog {
    .modal-card {
        max-width: 660px;
    }
}
.loading-overlay {
    .loading-background {
        background-color: whitesmoke;
        + div {
            margin-top: -100px;
            z-index: 999;
        }
    }
}
</style>
